/**
 * Threshold at which an override is called out as "aggressive": a price more than this percentage
 * below the AI's recommendation. Percent, not fraction, so 30 = 30%.
 *
 * Shared by the cell's warning icon and the drawer's "Supplier perception" row so a single edit is
 * described the same way in both places — two thresholds would tell the buyer two different
 * stories about one number.
 */
export const AGGRESSIVE_OVERRIDE_THRESHOLD_PERCENT = 30;

/**
 * How far below the AI suggestion a price sits, as a percentage of that suggestion. Negative when
 * the price is above it. Null when either figure is missing or the AI suggestion is not positive,
 * since there is then nothing meaningful to compare against.
 */
export const percentBelowAiSuggestion = (
  price: number | null,
  aiValue: number | null,
): number | null => {
  if (price == null || aiValue == null || aiValue <= 0) return null;

  return ((aiValue - price) / aiValue) * 100;
};

/**
 * True when the price is more than the threshold BELOW the AI's recommendation — a deeper discount
 * than the model expects suppliers to accept. Drives the cell's warning icon.
 */
export const isAggressiveOverride = (price: number | null, aiValue: number | null): boolean => {
  const percentBelow = percentBelowAiSuggestion(price, aiValue);

  return percentBelow != null && percentBelow > AGGRESSIVE_OVERRIDE_THRESHOLD_PERCENT;
};

/**
 * True when the price is more than the threshold ABOVE the AI's recommendation.
 *
 * The mirror of `isAggressiveOverride`, and easy to miss: `percentBelowAiSuggestion` goes negative
 * once the price rises above the suggestion, so a single `> threshold` check never catches this
 * side and a too-high override reads as "within a reasonable range".
 */
export const isConservativeOverride = (price: number | null, aiValue: number | null): boolean => {
  const percentBelow = percentBelowAiSuggestion(price, aiValue);

  return percentBelow != null && percentBelow < -AGGRESSIVE_OVERRIDE_THRESHOLD_PERCENT;
};
